import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiGet, apiPost } from '../utils/apiUtils';
import { API_URLS, GCS_BUCKET_URL } from '../config/api';
import LoadingSpinner from './LoadingSpinner';
import { t } from '../i18n';

const GamePlay = () => {
  const { novelId, gameStateId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [gameState, setGameState] = useState(null);
  const [userMessage, setUserMessage] = useState('');
  const [sending, setSending] = useState(false);

  // Fetch game state on component mount
  useEffect(() => {
    fetchGameState();
  }, [gameStateId]);


  const fetchGameState = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiGet(`/russian/game/${novelId}/state/${gameStateId}`);
      setGameState(response.data);

    } catch (err) {
      console.error('Error fetching game state:', err);
      setError(t('Ошибка загрузки игры'));
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = async () => {
    try {
      setSending(true);
      setError(null);

      // Send a UserRequest object with user_message: null for static scenes
      const response = await apiPost(`/russian/game/${novelId}/state/${gameStateId}`, {
        user_message: null
      });

      // Navigate to the new game state
      navigate(`/game/${novelId}/state/${response.data.game_state_id}`);

    } catch (err) {
      console.error('Error continuing game:', err);
      setError(t('Ошибка взаимодействия с игрой'));
      setSending(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (!userMessage.trim() || sending) return;

    try {
      setSending(true);
      setError(null);

      const response = await apiPost(`/russian/game/${novelId}/state/${gameStateId}`, {
        user_message: userMessage
      });

      // Clear the input and navigate to the new game state
      setUserMessage('');
      navigate(`/game/${novelId}/state/${response.data.game_state_id}`);

    } catch (err) {
      console.error('Error sending message:', err);
      setError(t('Ошибка взаимодействия с игрой'));
      setSending(false);
    }
  };

  const handleBackToNovel = () => {
    // Navigate back to the production novel page
    // We need to get the play_link from the game state
    if (gameState && gameState.novel && gameState.novel.play_link) {
      navigate(`/production/${gameState.novel.play_link}`);
    } else {
      // Fallback to home page if play_link is not available
      navigate(`/`);
    }
  };

  if (loading) {
    return (
      <div className="game-loading">
        <LoadingSpinner />
        <p>{t('Загрузка игры...')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="game-error">
        <p>{error}</p>
        <div className="game-error-actions">
          <button onClick={fetchGameState}>{t('Повторить')}</button>
          <button onClick={handleBackToNovel}>{t('Вернуться к новелле')}</button>
        </div>
      </div>
    );
  }

  if (!gameState) {
    return null;
  }

  return (
    <div className="game-container">
      {/* Background */}
      {gameState.background && (
        <div
          className="game-background"
          style={{ backgroundImage: `url(${gameState.background.url})` }}
        />
      )}

      {/* Characters */}
      <div className="game-characters">
        {gameState.sprites && gameState.sprites.map((sprite, index) => (
          <div
            key={index}
            className="game-character"
            style={{
              backgroundImage: `url(${sprite.url})`,
              left: `${(index + 1) * 20}%`
            }}
          />
        ))}
      </div>

      {/* Message box */}
      <div className="game-message-box">
        {gameState.speaker && (
          <div className="game-speaker">{gameState.speaker}</div>
        )}

        {gameState.message && (
          <div className="game-message">{gameState.message}</div>
        )}

        {/* Game ended state */}
        {gameState.the_end && (
          <div className="game-ended">
            <div className="game-ended-message">{t('Конец')}</div>
            <button onClick={handleBackToNovel} className="game-back-button">
              {t('Вернуться к новелле')}
            </button>
          </div>
        )}

        {/* Static scene continue button */}
        {!gameState.the_end && gameState.current_scene_type === 'STATIC' && (
          <button
            onClick={handleContinue}
            className="game-continue-button"
            disabled={sending}
          >
            {sending ? t('Отправка...') : t('Продолжить')}
          </button>
        )}

        {/* Interactive scene input */}
        {!gameState.the_end && gameState.current_scene_type === 'INTERACTIVE' && (
          <form onSubmit={handleSendMessage} className="game-input-form">
            <input
              type="text"
              value={userMessage}
              onChange={(e) => setUserMessage(e.target.value)}
              placeholder={t('Введите ваше сообщение...')}
              disabled={sending}
              className="game-input"
            />
            <button
              type="submit"
              disabled={!userMessage.trim() || sending}
              className="game-send-button"
            >
              {sending ? t('Отправка...') : t('Отправить')}
            </button>
          </form>
        )}
      </div>

      {/* Audio player for background music */}
      {gameState.music && gameState.music.url && (
        <audio
          src={gameState.music.url}
          autoPlay
          loop
          style={{ display: 'none' }}
        />
      )}
    </div>
  );
};

export default GamePlay;
